import { ClipboardEvent, FormEvent, KeyboardEvent, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Check,
  CircleHelp,
  Download,
  ExternalLink,
  FileText,
  FolderOpen,
  Headphones,
  Lock,
  LockKeyhole,
  Mail,
  RefreshCw,
  Search,
  ShieldCheck,
} from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { LinfinityLogo } from "@/assets/logos";
import {
  normalizeSuburbLookupValue,
  searchSuburbOptions,
  type SuburbLookupRow,
} from "@/services/suburbLookup";
import {
  publicProjectFilesService,
  type PublicProjectIfcModelSummary,
  type PublicProjectFileSummary,
  type ProjectFileSessionBootstrap,
} from "@/services/projectFilesService";
import "./ProjectFilesPortalPage.css";

const PROJECT_FILES_PUBLIC_BASE_PATH = "/projects/access";
const LINFINITY_CONTACT_URL = "https://linfinityeng.com/#contact";
const LINFINITY_CONTACT_PHONE = "0468 886 926";
const LINFINITY_CONTACT_EMAIL = "yl@linfinityeng.com";

type EntryStatus = "idle" | "sending" | "verifying";
type ProjectAccessFilter = "structural" | "civil" | "ifc";
type SessionState =
  | { status: "loading" }
  | { status: "ready"; data: ProjectFileSessionBootstrap }
  | { status: "error"; message: string };

const ROLE_OPTIONS = [
  { value: "architect", label: "Architect" },
  { value: "builder", label: "Builder" },
  { value: "building-surveyor", label: "Building Surveyor" },
  { value: "owner", label: "Owner" },
  { value: "inspector", label: "Inspector" },
  { value: "carpenter", label: "Carpenter" },
  { value: "plumber", label: "Plumber" },
  { value: "other", label: "Other" },
];

const ACCESS_FILTER_OPTIONS: Array<{ value: ProjectAccessFilter; label: string }> = [
  { value: "structural", label: "Structural document" },
  { value: "civil", label: "Civil document" },
  { value: "ifc", label: "IFC model" },
];

function formatSuburbOption(row: SuburbLookupRow): string {
  return [
    row.Suburb,
    row.PostCode ? String(row.PostCode) : null,
    row.State,
  ].filter(Boolean).join(", ");
}

function formatDate(value?: string | null) {
  if (!value) return "No date";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value.slice(0, 10);
  return date.toLocaleDateString("en-AU", { day: "2-digit", month: "short", year: "numeric" });
}

function formatFileSize(bytes?: number | null) {
  const value = Number(bytes || 0);
  if (!Number.isFinite(value) || value <= 0) return "Size unavailable";
  const units = ["B", "KB", "MB", "GB"];
  let next = value;
  let unitIndex = 0;
  while (next >= 1024 && unitIndex < units.length - 1) {
    next /= 1024;
    unitIndex += 1;
  }
  return `${next.toFixed(unitIndex === 0 ? 0 : 1)} ${units[unitIndex]}`;
}

function dateSortValue(value?: string | null): number {
  if (!value) return 0;
  const parsed = new Date(value).getTime();
  return Number.isFinite(parsed) ? parsed : 0;
}

function compareLatestProjectFile(a: PublicProjectFileSummary, b: PublicProjectFileSummary): number {
  return (
    dateSortValue(b.revisionDate) - dateSortValue(a.revisionDate)
    || dateSortValue(b.createdAt) - dateSortValue(a.createdAt)
    || b.fileName.localeCompare(a.fileName)
  );
}

function latestFilesForPackage(files: PublicProjectFileSummary[], packageType: "structural" | "civil") {
  const packageFiles = files.filter((file) => file.packageType === packageType);
  if (packageFiles.length === 0) return [];
  const latest = [...packageFiles].sort(compareLatestProjectFile)[0];
  return packageFiles
    .filter((file) => file.revisionDate === latest.revisionDate)
    .sort(compareLatestProjectFile);
}

function groupFiles(files: PublicProjectFileSummary[]) {
  const groups = new Map<string, {
    packageLabel: string;
    revisionDate: string;
    files: PublicProjectFileSummary[];
  }>();

  for (const file of files) {
    const key = `${file.packageType}:${file.revisionDate}`;
    const current = groups.get(key) || {
      packageLabel: file.packageLabel,
      revisionDate: file.revisionDate,
      files: [],
    };
    current.files.push(file);
    groups.set(key, current);
  }

  return Array.from(groups.values());
}

function OtpCodeInput({ value, onChange, disabled = false }: { value: string; onChange: (value: string) => void; disabled?: boolean }) {
  const inputsRef = useRef<Array<HTMLInputElement | null>>([]);
  const digits = Array.from({ length: 6 }, (_, index) => value[index] || "");

  const setDigitAt = (index: number, nextValue: string) => {
    const numeric = nextValue.replace(/\D/g, "");
    if (numeric.length > 1) {
      const nextCode = numeric.slice(0, 6);
      onChange(nextCode);
      inputsRef.current[Math.min(nextCode.length, 5)]?.focus();
      return;
    }

    const nextDigits = [...digits];
    nextDigits[index] = numeric;
    onChange(nextDigits.join("").slice(0, 6));
    if (numeric && index < 5) {
      inputsRef.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>, index: number) => {
    if (event.key === "Backspace" && !digits[index] && index > 0) {
      event.preventDefault();
      const nextDigits = [...digits];
      nextDigits[index - 1] = "";
      onChange(nextDigits.join(""));
      inputsRef.current[index - 1]?.focus();
    }
    if (event.key === "ArrowLeft" && index > 0) {
      event.preventDefault();
      inputsRef.current[index - 1]?.focus();
    }
    if (event.key === "ArrowRight" && index < 5) {
      event.preventDefault();
      inputsRef.current[index + 1]?.focus();
    }
  };

  const handlePaste = (event: ClipboardEvent<HTMLInputElement>) => {
    const pasted = event.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (!pasted) return;
    event.preventDefault();
    onChange(pasted);
    inputsRef.current[Math.min(pasted.length, 5)]?.focus();
  };

  return (
    <fieldset className="project-files-otp-code" aria-label="Email code">
      <legend>Email code</legend>
      <div className="project-files-otp-code__boxes">
        {digits.map((digit, index) => (
          <input
            key={index}
            ref={(element) => {
              inputsRef.current[index] = element;
            }}
            aria-label={`Email code digit ${index + 1}`}
            value={digit}
            onChange={(event) => setDigitAt(index, event.target.value)}
            onFocus={(event) => event.target.select()}
            onKeyDown={(event) => handleKeyDown(event, index)}
            onPaste={handlePaste}
            inputMode="numeric"
            autoComplete={index === 0 ? "one-time-code" : "off"}
            pattern="[0-9]*"
            maxLength={1}
            disabled={disabled}
          />
        ))}
      </div>
    </fieldset>
  );
}

function ProjectFilesEntry() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [projectRole, setProjectRole] = useState("builder");
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [projectNumber, setProjectNumber] = useState("");
  const [projectAddress, setProjectAddress] = useState("");
  const [suburb, setSuburb] = useState("");
  const [selectedSuburb, setSelectedSuburb] = useState<SuburbLookupRow | null>(null);
  const [suburbOptions, setSuburbOptions] = useState<SuburbLookupRow[]>([]);
  const [suburbLookupStatus, setSuburbLookupStatus] = useState<"idle" | "searching" | "ready" | "empty">("idle");
  const [isSuburbMenuOpen, setIsSuburbMenuOpen] = useState(false);
  const [otpCode, setOtpCode] = useState("");
  const [verificationId, setVerificationId] = useState("");
  const [matchedProjectLabel, setMatchedProjectLabel] = useState("");
  const [status, setStatus] = useState<EntryStatus>("idle");
  const [message, setMessage] = useState<string | null>(null);
  const entryStep = verificationId ? 2 : 1;
  const suburbLookupRequestRef = useRef(0);

  useEffect(() => {
    const query = suburb.trim();
    const selectedStillMatches = selectedSuburb
      ? normalizeSuburbLookupValue(selectedSuburb.Suburb) === normalizeSuburbLookupValue(query)
      : false;

    if (query.length < 2 || selectedStillMatches) {
      setSuburbOptions([]);
      setSuburbLookupStatus("idle");
      return undefined;
    }

    const requestId = suburbLookupRequestRef.current + 1;
    suburbLookupRequestRef.current = requestId;
    setSuburbLookupStatus("searching");

    const timer = window.setTimeout(() => {
      searchSuburbOptions(query, 8)
        .then((matches) => {
          if (suburbLookupRequestRef.current !== requestId) return;
          setSuburbOptions(matches);
          setSuburbLookupStatus(matches.length ? "ready" : "empty");
        })
        .catch(() => {
          if (suburbLookupRequestRef.current !== requestId) return;
          setSuburbOptions([]);
          setSuburbLookupStatus("empty");
        });
    }, 180);

    return () => {
      window.clearTimeout(timer);
    };
  }, [selectedSuburb, suburb]);

  const pickSuburb = (option: SuburbLookupRow) => {
    setSelectedSuburb(option);
    setSuburb(option.Suburb);
    setSuburbOptions([]);
    setSuburbLookupStatus("idle");
    setIsSuburbMenuOpen(false);
  };

  const requestAccess = async (event: FormEvent) => {
    event.preventDefault();
    setStatus("sending");
    setMessage(null);
    setVerificationId("");
    setOtpCode("");
    setMatchedProjectLabel("");
    const selectedSuburbIsCurrent = selectedSuburb
      ? normalizeSuburbLookupValue(selectedSuburb.Suburb) === normalizeSuburbLookupValue(suburb)
      : false;
    const suburbForRequest = selectedSuburbIsCurrent ? selectedSuburb?.Suburb || "" : "";

    if (!suburbForRequest) {
      setStatus("idle");
      setMessage("Pick a suburb from the search list before requesting access.");
      setIsSuburbMenuOpen(true);
      return;
    }

    try {
      const result = await publicProjectFilesService.requestAccess({
        email,
        projectRole,
        marketingConsent,
        projectNumber,
        projectAddress,
        suburb: suburbForRequest,
      });
      setVerificationId(result.verificationId);
      setMatchedProjectLabel(result.projectLabel);
      setMessage(`Matched ${result.projectLabel}. Email code sent.`);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "No project matched those details.");
    } finally {
      setStatus("idle");
    }
  };

  const verifyOtp = async (event: FormEvent) => {
    event.preventDefault();
    setStatus("verifying");
    setMessage(null);
    try {
      const result = await publicProjectFilesService.verifyOtp({
        verificationId,
        email,
        otpCode,
      });
      navigate(result.portalPath || `${PROJECT_FILES_PUBLIC_BASE_PATH}/${encodeURIComponent(result.sessionToken)}`, { replace: true });
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "The email code could not be verified.");
    } finally {
      setStatus("idle");
    }
  };

  return (
    <main className="project-files-public-page project-files-public-page--center">
      <section className="project-files-entry-panel" aria-label="Open project files and model">
        <div className="project-files-entry-panel__brand">
          <div className="project-files-entry-panel__logo">
            <LinfinityLogo size="horizontal" width={232} height={74} />
          </div>
          <div className="project-files-entry-panel__portal-title">
            <strong>Project access portal</strong>
            <span>Secure project file and model access</span>
          </div>
          <a
            className="project-files-entry-panel__support"
            href={LINFINITY_CONTACT_URL}
            target="_blank"
            rel="noreferrer"
            aria-label="Contact Linfinity Engineering on the Linfinity website"
          >
            <Headphones size={24} aria-hidden="true" />
            <span>Need assistance?</span>
            <strong>Contact Linfinity Engineering</strong>
            <small>{LINFINITY_CONTACT_PHONE} · {LINFINITY_CONTACT_EMAIL}</small>
          </a>
        </div>

        <div className="project-files-entry-steps" aria-label="Access steps">
          {[
            { id: 1, title: "Find project", detail: "Enter project details" },
            { id: 2, title: "Verify email", detail: "We will send you a code" },
            { id: 3, title: "Open files", detail: "Access latest issue" },
          ].map((step, index) => (
            <div
              key={step.id}
              className={`project-files-entry-step${entryStep === step.id ? " project-files-entry-step--active" : ""}${entryStep > step.id ? " project-files-entry-step--done" : ""}`}
            >
              <span className="project-files-entry-step__number">{entryStep > step.id ? <Check size={18} aria-hidden="true" /> : step.id}</span>
              <span className="project-files-entry-step__text">
                <strong>{step.title}</strong>
                <small>{step.detail}</small>
              </span>
              {index < 2 ? <span className="project-files-entry-step__line" aria-hidden="true" /> : null}
            </div>
          ))}
        </div>

        <div className="project-files-entry-layout">
          <div className="project-files-entry-main">
            <div className="project-files-entry-panel__header">
              <h1>{verificationId ? "Check your email" : "Find your project"}</h1>
              <p>
                {verificationId
                  ? "Enter the one-time code sent to your email address."
                  : "Enter the details below to securely find your project and request access."}
              </p>
            </div>

            {!verificationId && (
              <form className="project-files-entry-form" onSubmit={requestAccess}>
                <div className="project-files-entry-form__grid">
                  <label>
                    <span className="project-files-entry-form__label-line">
                      Email
                      <span className="project-files-entry-form__required">Required</span>
                    </span>
                    <input
                      value={email}
                      onChange={(event) => setEmail(event.target.value)}
                      autoComplete="email"
                      inputMode="email"
                      placeholder="name@company.com"
                      required
                    />
                  </label>
                  <label>
                    Project role
                    <select value={projectRole} onChange={(event) => setProjectRole(event.target.value)}>
                      {ROLE_OPTIONS.map((role) => <option key={role.value} value={role.value}>{role.label}</option>)}
                    </select>
                  </label>
                </div>

                <label className="project-files-entry-form__checkbox">
                  <input type="checkbox" checked={marketingConsent} onChange={(event) => setMarketingConsent(event.target.checked)} />
                  <span>I agree Linfinity can store my email and project role for future project and marketing contact.</span>
                </label>

                <div className="project-files-entry-form__grid">
                  <label>
                    <span className="project-files-entry-form__label-line">
                      Project number
                      <span className="project-files-entry-form__required">Required</span>
                    </span>
                    <input
                      value={projectNumber}
                      onChange={(event) => setProjectNumber(event.target.value)}
                      autoComplete="off"
                      placeholder="e.g. 12345"
                      required
                    />
                  </label>
                  <label>
                    <span className="project-files-entry-form__label-line">
                      Suburb
                      <span className="project-files-entry-form__required">Required</span>
                    </span>
                    <div className="project-files-suburb-combobox">
                      <input
                        value={suburb}
                        onChange={(event) => {
                          setSuburb(event.target.value);
                          setSelectedSuburb(null);
                          setIsSuburbMenuOpen(true);
                        }}
                        onFocus={() => setIsSuburbMenuOpen(true)}
                        onBlur={() => window.setTimeout(() => setIsSuburbMenuOpen(false), 120)}
                        autoComplete="off"
                        placeholder="e.g. Sample Suburb"
                        role="combobox"
                        aria-autocomplete="list"
                        aria-expanded={isSuburbMenuOpen && suburbLookupStatus !== "idle"}
                        aria-controls="project-files-suburb-options"
                        required
                      />
                      {isSuburbMenuOpen && suburbLookupStatus !== "idle" ? (
                        <div id="project-files-suburb-options" className="project-files-suburb-options" role="listbox">
                          {suburbLookupStatus === "searching" ? (
                            <div className="project-files-suburb-options__status">Searching suburbs</div>
                          ) : null}
                          {suburbLookupStatus === "empty" ? (
                            <div className="project-files-suburb-options__status">No suburb match found</div>
                          ) : null}
                          {suburbOptions.map((option) => (
                            <button
                              key={`${option.Suburb}-${option.PostCode}`}
                              type="button"
                              className="project-files-suburb-option"
                              role="option"
                              onMouseDown={(event) => event.preventDefault()}
                              onClick={() => pickSuburb(option)}
                            >
                              <strong>{option.Suburb}</strong>
                              <span>{[option.PostCode, option.State].filter(Boolean).join(", ")}</span>
                            </button>
                          ))}
                        </div>
                      ) : null}
                    </div>
                  </label>
                </div>
                <label>
                  <span className="project-files-entry-form__label-line">
                    Project street address
                    <span className="project-files-entry-form__required">Required</span>
                  </span>
                  <input
                    value={projectAddress}
                    onChange={(event) => setProjectAddress(event.target.value)}
                    autoComplete="off"
                    placeholder="e.g. 1 Sample Street"
                    required
                  />
                </label>
                <button type="submit" disabled={status !== "idle"}>
                  <Search size={20} aria-hidden="true" />
                  {status === "sending" ? "Sending code" : "Find project"}
                </button>
              </form>
            )}

            {verificationId && (
              <form className="project-files-entry-form project-files-entry-form--otp" onSubmit={verifyOtp}>
                {matchedProjectLabel ? <p className="project-files-entry-panel__hint">Matched: {matchedProjectLabel}</p> : null}
                <OtpCodeInput value={otpCode} onChange={setOtpCode} disabled={status !== "idle"} />
                <button type="submit" disabled={status !== "idle" || otpCode.length !== 6}>
                  <LockKeyhole size={18} aria-hidden="true" />
                  {status === "verifying" ? "Verifying" : "Open access"}
                </button>
                <button
                  type="button"
                  className="project-files-entry-form__secondary"
                  onClick={() => {
                    setVerificationId("");
                    setOtpCode("");
                    setMatchedProjectLabel("");
                    setMessage(null);
                  }}
                  disabled={status !== "idle"}
                >
                  Use different project details
                </button>
              </form>
            )}

            <p className="project-files-entry-panel__privacy">
              <ShieldCheck size={18} aria-hidden="true" />
              Your details are used only to verify access to this project.
            </p>

            {message && <p className="project-files-entry-panel__message">{message}</p>}
          </div>

          <aside className="project-files-entry-aside" aria-label="Project access information">
            <div className="project-files-entry-info-card">
              <div className="project-files-entry-info-card__icon" aria-hidden="true">
                <FileText size={28} />
              </div>
              <div>
                <strong>Important project information</strong>
                <p>3D views are indicative only. Refer to the issued architectural and engineering documentation for construction and clarification.</p>
              </div>
            </div>

            <div className="project-files-entry-reasons">
              <div className="project-files-entry-reasons__title">
                <Lock size={20} aria-hidden="true" />
                <strong>Why we need this</strong>
              </div>
              <ul>
                <li><Check size={18} aria-hidden="true" />We verify your details to protect project information</li>
                <li><Check size={18} aria-hidden="true" />You will receive a one-time access code by email</li>
                <li><Check size={18} aria-hidden="true" />Access is granted only after the project details match</li>
              </ul>
            </div>

            <div className="project-files-entry-secure">
              <ShieldCheck size={24} aria-hidden="true" />
              <div>
                <strong>Secure by design</strong>
                <p>Your information is protected and never shown to other visitors.</p>
              </div>
            </div>
          </aside>
        </div>

        <footer className="project-files-entry-footer">
          <div>
            <strong>Linfinity Engineering Pty Ltd</strong>
            <span>Project files and models prepared and issued by Linfinity Engineering</span>
          </div>
          <nav aria-label="Project access footer links">
            <span><Lock size={16} aria-hidden="true" />Privacy</span>
            <a href={LINFINITY_CONTACT_URL} target="_blank" rel="noreferrer"><Mail size={17} aria-hidden="true" />Contact</a>
            <span><CircleHelp size={17} aria-hidden="true" />Technical support</span>
          </nav>
        </footer>
      </section>
    </main>
  );
}

function ProjectFilesSession({ token }: { token: string }) {
  const [session, setSession] = useState<SessionState>({ status: "loading" });
  const [activeFilter, setActiveFilter] = useState<ProjectAccessFilter>("structural");
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [downloadingFileId, setDownloadingFileId] = useState<string | null>(null);
  const [openingIfcId, setOpeningIfcId] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const loadSession = async () => {
      try {
        setSession({ status: "loading" });
        const data = await publicProjectFilesService.getSession(token);
        if (!cancelled) {
          setSession({ status: "ready", data });
        }
      } catch (error) {
        if (!cancelled) {
          setSession({ status: "error", message: error instanceof Error ? error.message : "This project file session could not be opened." });
        }
      }
    };

    loadSession();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const latestDocumentFiles = useMemo(() => (
    session.status === "ready" && activeFilter !== "ifc"
      ? latestFilesForPackage(session.data.files, activeFilter)
      : []
  ), [activeFilter, session]);

  const groupedFiles = useMemo(() => groupFiles(latestDocumentFiles), [latestDocumentFiles]);

  const latestIfcModel = useMemo<PublicProjectIfcModelSummary | null>(() => (
    session.status === "ready" ? session.data.ifcModels?.[0] || null : null
  ), [session]);

  const activeFilterLabel = ACCESS_FILTER_OPTIONS.find((option) => option.value === activeFilter)?.label || "Project access";

  async function refreshSession() {
    setIsRefreshing(true);
    setMessage(null);
    try {
      const data = await publicProjectFilesService.getSession(token);
      setSession({ status: "ready", data });
      setMessage("Project files refreshed.");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "The latest project files could not be loaded.");
    } finally {
      setIsRefreshing(false);
    }
  }

  async function downloadFile(file: PublicProjectFileSummary) {
    setDownloadingFileId(file.id);
    setMessage(null);
    try {
      const result = await publicProjectFilesService.createDownloadLink(token, file.id);
      window.open(result.downloadUrl, "_blank", "noopener,noreferrer");
      setMessage(`Download link prepared for ${result.fileName}.`);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "The download link could not be prepared.");
    } finally {
      setDownloadingFileId(null);
    }
  }

  async function openIfcModel(model: PublicProjectIfcModelSummary) {
    setOpeningIfcId(model.ifcFileId);
    setMessage(null);
    try {
      const result = await publicProjectFilesService.createIfcViewerSession(token, model.ifcFileId);
      window.location.assign(result.viewerPath || `/projects/ifc/${encodeURIComponent(result.sessionToken)}`);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "The IFC model could not be opened.");
      setOpeningIfcId(null);
    }
  }

  if (session.status === "loading") {
    return (
      <main className="project-files-public-page project-files-public-page--center">
        <div className="project-files-loading-panel">
          <strong>Opening project files...</strong>
          <span>Checking the verified session.</span>
        </div>
      </main>
    );
  }

  if (session.status === "error") {
    return (
      <main className="project-files-public-page project-files-public-page--center">
        <div className="project-files-error-card">
          <h1>Project files unavailable</h1>
          <p>{session.message}</p>
        </div>
      </main>
    );
  }

  return (
    <main className="project-files-public-page">
      <header className="project-files-session-header">
        <div className="project-files-session-header__brand">
          <LinfinityLogo size="horizontal" width={184} height={58} />
        </div>
        <div className="project-files-session-header__title">
          <span>Project files</span>
          <h1>{session.data.projectLabel}</h1>
          <p>Session expires {formatDate(session.data.expiresAt)}.</p>
        </div>
      </header>

      <section className="project-files-session-body">
        <div className="project-files-session-toolbar">
          <div className="project-files-session-filter">
            <span id="project-access-filter-label" className="project-files-session-filter__label">Document type</span>
            <div className="project-files-session-filter__options" role="group" aria-labelledby="project-access-filter-label">
              {ACCESS_FILTER_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  className={`project-files-session-filter__option${activeFilter === option.value ? " project-files-session-filter__option--active" : ""}`}
                  aria-pressed={activeFilter === option.value}
                  onClick={() => {
                    setActiveFilter(option.value);
                    setMessage(null);
                  }}
                >
                  {option.value === "ifc" ? <Box size={17} aria-hidden="true" /> : <FileText size={17} aria-hidden="true" />}
                  <span>{option.label}</span>
                  {activeFilter === option.value ? (
                    <span className="project-files-session-filter__check" aria-hidden="true">
                      <Check size={13} />
                    </span>
                  ) : null}
                </button>
              ))}
            </div>
          </div>
          <div className="project-files-session-toolbar__actions">
            <span className="project-files-session-toolbar__status">Latest revision only</span>
            <button
              type="button"
              className="project-files-session-toolbar__refresh"
              onClick={refreshSession}
              disabled={isRefreshing}
            >
              <RefreshCw size={16} aria-hidden="true" className={isRefreshing ? "project-files-session-toolbar__refresh-icon--spinning" : undefined} />
              {isRefreshing ? "Refreshing" : "Refresh files"}
            </button>
          </div>
        </div>

        {activeFilter === "ifc" ? (
          latestIfcModel ? (
            <section className="project-files-group" aria-label="Latest IFC model">
              <div className="project-files-group__header">
                <div>
                  <span>IFC model</span>
                  <h2>{latestIfcModel.versionLabel || formatDate(latestIfcModel.versionCreatedAt)}</h2>
                </div>
                <strong>Latest model</strong>
              </div>
              <div className="project-files-file-list">
                <article className="project-files-file-row">
                  <div className="project-files-file-row__icon" aria-hidden="true">
                    <Box size={24} />
                  </div>
                  <div className="project-files-file-row__main">
                    <strong>{latestIfcModel.fileName}</strong>
                    <span>{formatFileSize(latestIfcModel.fileSizeBytes)} · Version {formatDate(latestIfcModel.versionCreatedAt)}</span>
                  </div>
                  <button type="button" onClick={() => openIfcModel(latestIfcModel)} disabled={Boolean(openingIfcId)}>
                    <ExternalLink size={17} aria-hidden="true" />
                    {openingIfcId === latestIfcModel.ifcFileId ? "Opening" : "Open model"}
                  </button>
                </article>
              </div>
            </section>
          ) : (
            <div className="project-files-empty-panel">
              <Box size={34} />
              <h2>No IFC model is published</h2>
              <p>This verified project does not have an IFC model yet.</p>
            </div>
          )
        ) : groupedFiles.length ? (
          <div className="project-files-group-list">
            {groupedFiles.map((group) => (
              <section key={`${group.packageLabel}-${group.revisionDate}`} className="project-files-group" aria-label={`${group.packageLabel} ${group.revisionDate}`}>
                <div className="project-files-group__header">
                  <div>
                    <span>{activeFilterLabel}</span>
                    <h2>{formatDate(group.revisionDate)}</h2>
                  </div>
                  <strong>{group.files.length} file{group.files.length === 1 ? "" : "s"}</strong>
                </div>
                <div className="project-files-file-list">
                  {group.files.map((file) => (
                    <article key={file.id} className="project-files-file-row">
                      <div className="project-files-file-row__icon" aria-hidden="true">
                        <FileText size={24} />
                      </div>
                      <div className="project-files-file-row__main">
                        <strong>{file.fileName}</strong>
                        <span>{formatFileSize(file.fileSizeBytes)} · Uploaded {formatDate(file.createdAt)}</span>
                      </div>
                      <button type="button" onClick={() => downloadFile(file)} disabled={Boolean(downloadingFileId)}>
                        <Download size={17} aria-hidden="true" />
                        {downloadingFileId === file.id ? "Preparing" : "Download"}
                      </button>
                    </article>
                  ))}
                </div>
              </section>
            ))}
          </div>
        ) : (
          <div className="project-files-empty-panel">
            <FolderOpen size={34} />
            <h2>No {activeFilterLabel.toLowerCase()} is published</h2>
            <p>This verified project does not have a latest {activeFilterLabel.toLowerCase()} yet.</p>
          </div>
        )}
        {message && <p className="project-files-session-message" role="status">{message}</p>}
      </section>
    </main>
  );
}

export default function ProjectFilesPortalPage() {
  const { token } = useParams();
  return token ? <ProjectFilesSession token={token} /> : <ProjectFilesEntry />;
}
